"use client";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { initializeAttributionSession } from "@/app/lib/attributionSnapshot";

type CalendlyClickMeta = Record<string, unknown>;

interface AppContextType {
  lightbox: string | null;
  openLightbox: (id: string) => void;
  closeLightbox: () => void;
  trackCalendlyClick: (meta?: CalendlyClickMeta) => void;
  menu: string | null;
  toggleMenu: (id: string | null) => void;
  translations: {
    en: string;
    cs: string;
  };
  handleTranslations: (en: string | null, cs: string | null) => void;
}

const defaultContext: AppContextType = {
  lightbox: null,
  openLightbox: () => {},
  closeLightbox: () => {},
  trackCalendlyClick: () => {},
  menu: null,
  toggleMenu: () => {},
  translations: {
    en: "",
    cs: "",
  },
  handleTranslations: () => {},
};

const AppContext = createContext<AppContextType>(defaultContext);

interface AppWrapperProps {
  children: ReactNode;
  data: any;
}

export const AppWrapper: React.FC<AppWrapperProps> = ({ children, data }) => {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  const [translations, setTranslations] = useState({
    en: "",
    cs: "",
  });

  useEffect(() => {
    initializeAttributionSession();
  }, []);

  // Consolidated useEffect to handle scroll-lock logic
  useEffect(() => {
    const disableScroll = lightbox || menu;
    document.documentElement.style.overflow = disableScroll ? "hidden" : "auto";

    return () => {
      document.documentElement.style.overflow = "auto";
    };
  }, [lightbox, menu]);

  // Memoize the functions to prevent unnecessary re-renders
  const openLightbox = useCallback((id: string) => {
    setLightbox(id);
    setMenu(null);
    window.dataLayer?.push({
      event: "open_form",
      form_name: id,
      page_path: window.location.pathname,
    });
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const trackCalendlyClick = useCallback((meta: CalendlyClickMeta = {}) => {
    window.dataLayer?.push({
      event: "calendly_click",
      page_path: window.location.pathname,
      ...meta,
    });
  }, []);

  const toggleMenu = useCallback((id: string | null) => {
    setMenu((prev) => (prev === id ? null : id));
  }, []);

  const handleTranslations = useCallback(
    (en: string | null, cs: string | null) => {
      setTranslations((prev) => {
        const newTranslations = { en: en ?? "", cs: cs ?? "" };
        if (prev.en !== newTranslations.en || prev.cs !== newTranslations.cs) {
          return newTranslations;
        }
        return prev;
      });
    },
    []
  );

  // Memoize the context value to avoid re-creation on every render
  const contextValue = useMemo(
    () => ({
      lightbox,
      openLightbox,
      closeLightbox,
      trackCalendlyClick,
      menu,
      toggleMenu,
      handleTranslations,
      translations,
    }),
    [
      lightbox,
      menu,
      openLightbox,
      closeLightbox,
      trackCalendlyClick,
      toggleMenu,
      handleTranslations,
      translations,
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// Custom hook to access the context
export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppWrapper");
  }
  return context;
}
